// @ts-nocheck
import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { toast } from 'sonner';
import { useWorkspace } from '@/contexts/context';
import { hasPermission, type UserRole } from '@saas-boilerplate/shared';
import { supabase, edgeFnUrl } from '@saas-boilerplate/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Plus,
  Settings,
  CheckCircle,
  XCircle,
  Loader2,
  MessageSquare,
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  Unplug,
  Hash,
  Plug,
  AlertTriangle,
  Activity,
  Send,
  Calendar,
} from 'lucide-react';
import { Textarea } from './ui/textarea';
import { storeSlackOAuthState, getWorkspaceIdFromSlackState } from '@/lib/slack-oauth';
import {
  buildGoogleCalendarAuthUrl,
  buildMicrosoftCalendarAuthUrl,
  consumeCalendarOAuthState,
  storeCalendarOAuthState,
} from '@/lib/calendar-oauth';

// edgeFnUrl now lives in @saas-boilerplate/shared so the URL honours
// VITE_SUPABASE_URL (custom domain) when set.

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CredentialField {
  key: string;
  label: string;
  placeholder: string;
  type?: 'text' | 'password' | 'url';
  helpText?: string;
}

interface IntegrationDefinition {
  id: string;
  name: string;
  description: string;
  icon: ReactNode;
  category: string;
  connectVia?: 'oauth' | 'credentials';
  steps: {
    setup: { title: string; instructions: ReactNode };
    credentials: { fields: CredentialField[] };
  };
  configFields?: CredentialField[];
}

type ConnectionStatus = 'connected' | 'available';

// ---------------------------------------------------------------------------
// Integration definitions
// ---------------------------------------------------------------------------

const INTEGRATIONS: IntegrationDefinition[] = [
  {
    id: 'slack',
    name: 'Slack',
    description: 'Connect Slack channels to automatically collect feedback from team discussions and receive notifications.',
    icon: <MessageSquare className="h-6 w-6" />,
    category: 'Communication',
    connectVia: 'oauth',
    steps: {
      setup: { title: '', instructions: null },
      credentials: { fields: [] },
    },
  },
];

// ---------------------------------------------------------------------------
// Slack OAuth constants
// ---------------------------------------------------------------------------

const SLACK_SCOPES = 'chat:write,channels:join,channels:history,channels:read,groups:history,groups:read,users:read,users:read.email,im:write,mpim:write';

const SLACK_STEP_LABELS = ['Add to Slack', 'Configure', 'Test'];

function SlackStepIndicator({ current, connected }: { current: number; connected: boolean }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-6">
      {SLACK_STEP_LABELS.map((label, idx) => {
        const stepNum = idx + 1;
        const isActive = stepNum === current;
        const isDone = (stepNum === 1 && connected) || stepNum < current;
        return (
          <div key={label} className="flex items-center">
            {idx > 0 && (
              <div className={`w-12 h-0.5 ${isDone ? 'bg-primary' : 'bg-border'}`} />
            )}
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium border-2 transition-colors ${
                  isDone
                    ? 'bg-primary border-primary text-primary-foreground'
                    : isActive
                      ? 'border-primary text-primary bg-background'
                      : 'border-border text-muted-foreground bg-background'
                }`}
              >
                {isDone ? <CheckCircle className="h-4 w-4" /> : stepNum}
              </div>
              <span className={`text-[11px] whitespace-nowrap ${isActive || isDone ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                {label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Slack Connect Wizard (3-step: Add to Slack → Configure → Test)
// ---------------------------------------------------------------------------

function SlackConnectWizard({
  open,
  onOpenChange,
  isConnected,
  onDisconnect,
  onConnected,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isConnected: boolean;
  onDisconnect: () => void;
  onConnected: () => void;
}) {
  const { workspace, membership } = useWorkspace();
  const clientId = import.meta.env.VITE_PUBLIC_SLACK_CLIENT_ID;

  const [step, setStep] = useState(isConnected ? 2 : 1);
  const [slackChannelId, setSlackChannelId] = useState<string | null>(null);
  const [slackChannels, setSlackChannels] = useState<{ id: string; name: string; isPrivate: boolean }[]>([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [channelSaving, setChannelSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<'idle' | 'success' | 'error'>('idle');
  const [manualInviteNeeded, setManualInviteNeeded] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  useEffect(() => {
    if (isConnected && step === 1) setStep(2);
  }, [isConnected, step]);

  const loadChannels = useCallback(async () => {
    if (!workspace) return;
    setChannelsLoading(true);
    try {
      const fnUrl = edgeFnUrl('slack-channels');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const res = await fetch(fnUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ workspace_id: workspace.id }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to load channels');
      setSlackChannels(result.channels || []);
    } catch {
      toast.error('Failed to load Slack channels');
    } finally {
      setChannelsLoading(false);
    }
  }, [workspace?.id]);

  const fetchCurrentChannel = useCallback(async () => {
    if (!workspace) return;
    const { data } = await supabase.rpc('get_slack_connection_status', { p_workspace_id: workspace.id });
    const row = Array.isArray(data) ? data[0] : data;
    setSlackChannelId(row?.slack_channel_id || null);
  }, [workspace?.id]);

  useEffect(() => {
    if (open) {
      if (isConnected) {
        fetchCurrentChannel();
        loadChannels();
      }
    }
  }, [open, isConnected, fetchCurrentChannel, loadChannels]);

  const joinBotToChannel = async (channelId: string): Promise<{ joined: boolean; needsManualInvite: boolean }> => {
    try {
      const fnUrl = edgeFnUrl('slack-channels');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return { joined: false, needsManualInvite: false };
      const res = await fetch(fnUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ workspace_id: workspace!.id, action: 'join_channel', channel_id: channelId }),
      });
      const result = await res.json();
      return { joined: !!result.joined, needsManualInvite: !!result.needs_manual_invite };
    } catch {
      return { joined: false, needsManualInvite: false };
    }
  };

  const handleAddToSlack = () => {
    if (!clientId || !workspace) return;
    const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
    const redirectUri = `${appUrl.replace(/\/$/, '')}/integrations`;
    const state = storeSlackOAuthState(workspace.id);
    window.location.href = `https://slack.com/oauth/v2/authorize?client_id=${clientId}&scope=${encodeURIComponent(SLACK_SCOPES)}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(state)}`;
  };

  const handleChannelChange = async (channelId: string) => {
    if (!workspace) return;
    setChannelSaving(true);
    setManualInviteNeeded(false);
    try {
      const { error } = await supabase.rpc('update_slack_notification_channel', {
        p_workspace_id: workspace.id,
        p_slack_channel_id: channelId === '_none' ? null : channelId,
      });
      if (error) throw error;
      setSlackChannelId(channelId === '_none' ? null : channelId);
      if (channelId !== '_none') {
        const { joined, needsManualInvite } = await joinBotToChannel(channelId);
        if (joined) toast.success('Channel saved. Bot added.');
        else if (needsManualInvite) {
          setManualInviteNeeded(true);
          const ch = slackChannels.find((c) => c.id === channelId);
          toast.info(
            'Channel saved. Invite your Slack app’s bot to #' +
              (ch?.name || 'channel') +
              ' (in Slack: /invite @YourBotName - use your app’s bot display name).',
          );
        } else toast.success('Channel saved');
      } else toast.success('Channel removed');
    } catch (e: any) {
      toast.error('Failed to update channel', { description: e?.message });
    } finally {
      setChannelSaving(false);
    }
  };

  const handleSaveConfig = () => {
    // Workspace Slack setup is intentionally minimal: install + (optional) default channel.
    // Per-meeting routing and timing live on the meeting itself, not on the workspace.
    setStep(3);
  };

  const handleTest = async () => {
    if (!workspace) return;
    setTesting(true);
    setTestResult('idle');
    try {
      const { data } = await supabase.rpc('get_slack_connection_status', { p_workspace_id: workspace.id });
      const row = Array.isArray(data) ? data[0] : data;
      if (!row?.slack_team_id) throw new Error('No connection');
      if (slackChannelId) {
        const { joined, needsManualInvite } = await joinBotToChannel(slackChannelId);
        if (!joined && needsManualInvite) {
          setManualInviteNeeded(true);
          setTestResult('error');
          return;
        }
      }
      setManualInviteNeeded(false);
      setTestResult('success');
    } catch {
      setTestResult('error');
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = () => {
    onDisconnect();
    setConfirmDisconnect(false);
    onOpenChange(false);
    toast.success('Slack disconnected.');
  };

  const handleDone = () => {
    onConnected();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-muted rounded-lg">
              <MessageSquare className="h-6 w-6" />
            </div>
            <div>
              <DialogTitle>Connect Slack</DialogTitle>
              <DialogDescription>Connect your Slack workspace to SaaS App.</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <SlackStepIndicator current={step} connected={isConnected} />

        {/* Step 1: Add to Slack */}
        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Connect your Slack workspace to SaaS App to enable:
            </p>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span><strong>Send to SaaS App</strong> message shortcut for forwarding conversations as feedback</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Notifications for new feedback and ideas in your chosen channel</span>
              </li>
            </ul>
            <div className="rounded-lg border bg-muted/30 overflow-hidden flex justify-center p-4">
              <img src="/upstand_ai_logo.svg" alt="Upstand.ai" className="w-full max-w-xs object-contain max-h-24 opacity-90" />
            </div>
            <p className="text-sm text-muted-foreground">
              Clicking the button below will take you to Slack to authorize SaaS App. Once approved, the app will be installed to your workspace.
            </p>
            <Button onClick={handleAddToSlack} disabled={!clientId} className="w-full">
              {!clientId ? 'Slack Client ID not configured' : 'Add to Slack'}
              {clientId && <ExternalLink className="h-4 w-4 ml-2" />}
            </Button>
          </div>
        )}

        {/* Step 2: Configure */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Hash className="h-4 w-4" /> Notification Channel
              </Label>
              <Select value={slackChannelId || '_none'} onValueChange={handleChannelChange} disabled={channelSaving}>
                <SelectTrigger>
                  <SelectValue placeholder={channelsLoading ? 'Loading...' : 'Select a channel'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">No channel selected</SelectItem>
                  {slackChannels.map((ch) => (
                    <SelectItem key={ch.id} value={ch.id}>
                      #{ch.name} {ch.isPrivate ? '(private)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {manualInviteNeeded && slackChannelId && (
              <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 p-3">
                <MessageSquare className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  Private channel: run <code className="bg-amber-100 dark:bg-amber-900/50 px-1 rounded text-xs">/invite @YourSlackBot</code> there (use your Slack app’s bot username).
                </p>
              </div>
            )}
            <Button onClick={handleSaveConfig} disabled={channelSaving} className="w-full">
              {channelSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </div>
        )}

        {/* Step 3: Test */}
        {step === 3 && (
          <div className="flex flex-col items-center justify-center py-6 space-y-4">
            <CheckCircle className="h-12 w-12 text-green-500" />
            <p className="font-medium">Integration complete</p>
            <p className="text-sm text-muted-foreground text-center">Test the connection to verify everything works.</p>
            <Button variant="outline" onClick={handleTest} disabled={testing}>
              {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Test connection
            </Button>
            {testResult === 'success' && <p className="text-sm text-green-600 flex items-center gap-1"><CheckCircle className="h-4 w-4" /> Connection verified.</p>}
            {testResult === 'error' && <p className="text-sm text-destructive flex items-center gap-1"><XCircle className="h-4 w-4" /> Connection check failed.</p>}
          </div>
        )}

        <DialogFooter className="flex justify-between sm:justify-between">
          <div>
            {step > 1 && step < 3 && (
              <Button variant="ghost" size="sm" onClick={() => setStep(step - 1)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
            {step === 3 && (
              <Button variant="ghost" size="sm" onClick={() => setStep(2)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {step === 1 && (
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            )}
            {step === 2 && (
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            )}
            {step === 3 && (
              <Button onClick={handleDone}>Done</Button>
            )}
          </div>
        </DialogFooter>

        {isConnected && !confirmDisconnect && (
          <div className="pt-2 border-t">
            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setConfirmDisconnect(true)}>
              <Unplug className="h-4 w-4 mr-1" /> Disconnect Slack
            </Button>
          </div>
        )}
        {confirmDisconnect && (
          <div className="flex items-center justify-between bg-destructive/10 p-3 rounded-lg mt-2">
            <p className="text-sm text-destructive">Remove the integration?</p>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setConfirmDisconnect(false)}>Cancel</Button>
              <Button variant="destructive" size="sm" onClick={handleDisconnect}>Disconnect</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

function getStatusIcon(status: ConnectionStatus) {
  if (status === 'connected') return <CheckCircle className="h-4 w-4 text-green-500" />;
  return <div className="h-4 w-4 rounded-full border-2 border-border" />;
}

// ---------------------------------------------------------------------------
// Integration Request Form
// ---------------------------------------------------------------------------

function IntegrationRequestForm() {
  const { workspace, userProfile } = useWorkspace();
  const [integrationName, setIntegrationName] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workspace) return;
    setSubmitting(true);
    try {
      const fnUrl = edgeFnUrl('integration-request');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Please sign in to submit a request');
        return;
      }
      const res = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          integration_name: integrationName.trim() || undefined,
          message: message.trim() || undefined,
          user_name: userProfile?.full_name,
          user_email: userProfile?.email,
        }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to send request');
      toast.success('Request sent', { description: "We'll be in touch about your integration request." });
      setIntegrationName('');
      setMessage('');
    } catch (e: any) {
      toast.error('Failed to send request', { description: e?.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-muted rounded-lg">
            <Plug className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <CardTitle className="text-base">Request an integration</CardTitle>
            <CardDescription>Don't see the integration you need? Tell us what you'd like to connect.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="integration-request-name">Integration or tool name</Label>
            <Input
              id="integration-request-name"
              placeholder="e.g. Linear, Asana, HubSpot"
              value={integrationName}
              onChange={(e) => setIntegrationName(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="integration-request-message">Message</Label>
            <Textarea
              id="integration-request-message"
              placeholder="Describe what you'd like to integrate and how you'd use it..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              disabled={submitting}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Your details ({userProfile?.full_name || 'name'}, {userProfile?.email || 'email'}) will be included with the request.
          </p>
          <Button type="submit" disabled={submitting}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Send request
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function Integrations() {
  const { workspace, membership } = useWorkspace();
  const userRole = membership?.role as UserRole;

  const [statuses, setStatuses] = useState<Record<string, ConnectionStatus>>({
    slack: 'available',
  });

  const [calendarRows, setCalendarRows] = useState<
    {
      id: string;
      provider: string;
      external_account_email: string | null;
      token_expires_at: string | null;
      scopes: string | null;
      needs_reconnect: boolean;
    }[]
  >([]);

  const [connectingId, setConnectingId] = useState<string | null>(null);
  const [configuringId, setConfiguringId] = useState<string | null>(null);
  const [oauthInProgress, setOauthInProgress] = useState(false);
  const [integrationStats, setIntegrationStats] = useState<{ errors_last_week: number; events_processed: number } | null>(null);

  const fetchIntegrationStats = useCallback(async () => {
    if (!workspace?.id) return;
    try {
      const { data } = await supabase.rpc('get_integration_stats', { p_workspace_id: workspace.id });
      if (data) setIntegrationStats(data as any);
    } catch { /* silent */ }
  }, [workspace?.id]);

  useEffect(() => {
    fetchIntegrationStats();
  }, [fetchIntegrationStats]);

  // Fetch real Slack status from DB
  const fetchSlackStatus = useCallback(async (forWorkspaceId?: string) => {
    const id = forWorkspaceId ?? workspace?.id;
    if (!id) return;
    const { data } = await supabase.rpc('get_slack_connection_status', { p_workspace_id: id });
    const row = Array.isArray(data) ? data[0] : data;
    if (id === workspace?.id) {
      setStatuses((prev) => ({
        ...prev,
        slack: row?.slack_team_id ? 'connected' : 'available',
      }));
    }
  }, [workspace?.id]);

  useEffect(() => {
    fetchSlackStatus();
  }, [fetchSlackStatus]);

  const fetchCalendarConnections = useCallback(async () => {
    if (!workspace?.id) return;
    const { data } = await supabase
      .from('calendar_connections')
      .select('id, provider, external_account_email, token_expires_at, scopes, needs_reconnect')
      .eq('workspace_id', workspace.id);
    setCalendarRows(data || []);
  }, [workspace?.id]);

  useEffect(() => {
    fetchCalendarConnections();
  }, [fetchCalendarConnections]);

  // Handle OAuth callbacks (?code=...&state=... on /integrations) - calendar first, then Slack
  const oauthProcessedRef = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state || oauthProcessedRef.current) return;

    const calCtx = consumeCalendarOAuthState(state);
    if (calCtx) {
      oauthProcessedRef.current = true;
      const run = async () => {
        setOauthInProgress(true);
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.access_token) {
            toast.error('Please sign in to connect your calendar');
            oauthProcessedRef.current = false;
            return;
          }
          const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
          const redirectUri = `${appUrl.replace(/\/$/, '')}/integrations`;
          const res = await fetch(edgeFnUrl('calendar-oauth-exchange'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              apikey: import.meta.env.VITE_SUPABASE_ANON_KEY ?? '',
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              provider: calCtx.provider,
              code,
              workspace_id: calCtx.workspace_id,
              redirect_uri: redirectUri,
            }),
          });
          const result = await res.json();
          if (!res.ok) throw new Error(result.error || 'Calendar connection failed');
          await fetchCalendarConnections();
          toast.success(`${calCtx.provider === 'google' ? 'Google' : 'Microsoft'} calendar connected`, {
            description: result.external_account_email || 'You can pick meetings under Meetings → Enrol.',
          });
          window.history.replaceState({}, '', window.location.pathname || '/');
        } catch (e: any) {
          toast.error('Calendar connection failed', { description: e?.message });
          oauthProcessedRef.current = false;
        } finally {
          setOauthInProgress(false);
        }
      };
      run();
      return;
    }

    const workspaceId = getWorkspaceIdFromSlackState(state);
    if (!workspaceId) {
      toast.error('Connection expired or invalid. Try again from Integrations.');
      window.history.replaceState({}, '', window.location.pathname || '/');
      return;
    }

    oauthProcessedRef.current = true;

    const exchangeAndSave = async () => {
      setOauthInProgress(true);
      try {
        const fnUrl = edgeFnUrl('slack-oauth-callback');
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          toast.error('Please sign in to connect Slack');
          oauthProcessedRef.current = false;
          return;
        }
        const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
        const redirectUri = `${appUrl.replace(/\/$/, '')}/integrations`;
        const res = await fetch(fnUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ code, state, workspace_id: workspaceId, redirect_uri: redirectUri }),
        });
        const result = await res.json();
        if (!res.ok) throw new Error(result.error || 'Failed to connect Slack');

        await fetchSlackStatus(workspaceId);
        toast.success('Slack connected!', { description: 'Click Configure to select a notification channel.' });
        window.history.replaceState({}, '', window.location.pathname || '/');
        setConfiguringId('slack');
      } catch (e: any) {
        toast.error('Failed to connect Slack', { description: e?.message });
        oauthProcessedRef.current = false;
      } finally {
        setOauthInProgress(false);
      }
    };

    exchangeAndSave();
  }, [fetchSlackStatus, fetchCalendarConnections]);

  const connectedCount =
    Object.values(statuses).filter((s) => s === 'connected').length + calendarRows.length;

  const googleCal = calendarRows.find((r) => r.provider === 'google');
  const msCal = calendarRows.find((r) => r.provider === 'microsoft');

  const startCalendarOAuth = (provider: 'google' | 'microsoft') => {
    if (!workspace?.id) return;
    const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
    const redirectUri = `${appUrl.replace(/\/$/, '')}/integrations`;
    const state = storeCalendarOAuthState(workspace.id, provider);
    if (provider === 'google') {
      const cid = import.meta.env.VITE_GOOGLE_CALENDAR_CLIENT_ID;
      if (!cid) {
        toast.error('Google OAuth client ID is not configured (VITE_GOOGLE_CALENDAR_CLIENT_ID).');
        return;
      }
      window.location.href = buildGoogleCalendarAuthUrl({ clientId: cid, redirectUri, state });
      return;
    }
    const mid = import.meta.env.VITE_MICROSOFT_CALENDAR_CLIENT_ID;
    if (!mid) {
      toast.error('Microsoft OAuth client ID is not configured (VITE_MICROSOFT_CALENDAR_CLIENT_ID).');
      return;
    }
    window.location.href = buildMicrosoftCalendarAuthUrl({ clientId: mid, redirectUri, state });
  };

  const disconnectCalendar = async (id: string) => {
    const { error } = await supabase.from('calendar_connections').delete().eq('id', id);
    if (error) toast.error(error.message);
    else {
      toast.success('Calendar disconnected');
      fetchCalendarConnections();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1>Integrations</h1>
        <p className="text-muted-foreground">Connect external tools to collect feedback and sync data.</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-950/40">
                <Plug className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{connectedCount}</p>
                <p className="text-xs text-muted-foreground">Active integrations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${(integrationStats?.errors_last_week ?? 0) > 0 ? 'bg-red-100 dark:bg-red-950/40' : 'bg-muted'}`}>
                <AlertTriangle className={`h-5 w-5 ${(integrationStats?.errors_last_week ?? 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`} />
              </div>
              <div>
                <p className="text-2xl font-bold">{integrationStats?.errors_last_week ?? '–'}</p>
                <p className="text-xs text-muted-foreground">Errors (last 7 days)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-950/40">
                <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{integrationStats?.events_processed?.toLocaleString() ?? '–'}</p>
                <p className="text-xs text-muted-foreground">Events processed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* OAuth in-progress overlay */}
      {oauthInProgress && (
        <Card className="border-primary/50">
          <CardContent className="flex items-center justify-center gap-3 py-6">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Connecting…</p>
          </CardContent>
        </Card>
      )}

      {/* Per-user calendar (Phase 1) */}
      <div>
        <h2 className="text-lg font-medium mb-2">Personal calendar</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Connect your own Google or Microsoft account to pick meetings during enrolment and refresh attendees when APIs are configured.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                <CardTitle className="text-base">Google Calendar</CardTitle>
              </div>
              <CardDescription>
                {googleCal?.external_account_email || (googleCal ? 'Connected' : 'Not connected')}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              {googleCal?.needs_reconnect ? (
                <p className="rounded-md border border-rose-300/50 bg-rose-50 px-3 py-2 text-xs text-rose-900">
                  <strong>Action needed:</strong> your Google connection is no longer valid
                  (token rejected by Google). Reconnect to restore calendar sync. Your meeting
                  history and settings are unaffected.
                </p>
              ) : null}
              <div className="flex gap-2">
                {googleCal ? (
                  <>
                    <Button
                      size="sm"
                      onClick={() => startCalendarOAuth('google')}
                      disabled={!hasPermission(userRole, 'canConfigureIntegrations')}
                    >
                      {googleCal.needs_reconnect ? 'Reconnect now' : 'Reconnect'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => disconnectCalendar(googleCal.id)}>
                      Disconnect
                    </Button>
                  </>
                ) : (
                  <Button size="sm" onClick={() => startCalendarOAuth('google')} disabled={!hasPermission(userRole, 'canConfigureIntegrations')}>
                    Connect Google
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                <CardTitle className="text-base">Microsoft 365</CardTitle>
              </div>
              <CardDescription>
                {msCal?.external_account_email || (msCal ? 'Connected' : 'Not connected')}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex gap-2">
              {msCal ? (
                <Button variant="outline" size="sm" onClick={() => disconnectCalendar(msCal.id)}>
                  Disconnect
                </Button>
              ) : (
                <Button size="sm" onClick={() => startCalendarOAuth('microsoft')} disabled={!hasPermission(userRole, 'canConfigureIntegrations')}>
                  Connect Microsoft
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Integrations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {INTEGRATIONS.map((integration) => {
          const status = statuses[integration.id] || 'available';
          const isConnected = status === 'connected';

          return (
            <Card
              key={integration.id}
              className={`transition-all duration-200 hover:shadow-md ${isConnected ? 'border-green-200' : ''}`}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-muted rounded-lg">{integration.icon}</div>
                    <div>
                      <CardTitle className="text-base">{integration.name}</CardTitle>
                      <Badge variant="outline" className="text-xs mt-1">{integration.category}</Badge>
                    </div>
                  </div>
                  {getStatusIcon(status)}
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="mb-4">{integration.description}</CardDescription>

                <div className="flex space-x-2">
                  {isConnected ? (
                    hasPermission(userRole, 'canConfigureIntegrations') && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => setConfiguringId(integration.id)}
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        Configure
                      </Button>
                    )
                  ) : (
                    hasPermission(userRole, 'canConfigureIntegrations') && (
                      <Button size="sm" className="flex-1" onClick={() => setConnectingId(integration.id)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Connect
                      </Button>
                    )
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Integration Request Form */}
      <IntegrationRequestForm />

      {/* Slack Connect/Configure Wizard */}
      {(connectingId === 'slack' || configuringId === 'slack') && (
        <SlackConnectWizard
          open
          onOpenChange={(v) => {
            if (!v) {
              setConnectingId(null);
              setConfiguringId(null);
            }
          }}
          isConnected={statuses.slack === 'connected'}
          onDisconnect={() => {
            setStatuses((prev) => ({ ...prev, slack: 'available' }));
            setConfiguringId(null);
            setConnectingId(null);
          }}
          onConnected={() => {
            setStatuses((prev) => ({ ...prev, slack: 'connected' }));
            setConnectingId(null);
            setConfiguringId(null);
          }}
        />
      )}
    </div>
  );
}
